import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApprovalGuideModal } from '@app/providers/ApprovalGuideModalProvider';

import useSidebarStore from '@entities/ui/model/useSidebarStore';
import useIsMobile from '@shared/hooks/useIsMobile';
import BoxIcon from '@shared/assets/images/box_icon.svg';
import ChevronRightIcon from '@shared/assets/images/chevron-right_icon.svg';
import InboxIcon from '@shared/assets/images/inbox-01_icon.svg';
import SettingsIcon from '@shared/assets/images/settings-01_icon.svg';
import UmbrellaIcon from '@shared/assets/images/umbrella-03_icon.svg';
import ApprovalNotificationDot from '@shared/components/atoms/ApprovalNotificationDot/indes';
import TabContentLayout from '../TabContentLayout/TabContentLayout';
import Modal from '@shared/components/atoms/Modal/Modal';
import InquiryFormModal from '@shared/components/modal/InquiryFormModal';
import InquiryListModal from '@shared/components/modal/InquiryListModal';
import Alert from '@shared/components/atoms/Alert';

import styles from './Sidebar.module.scss';

/**
 * URL 경로를 기반으로 현재 활성 메뉴 키를 반환합니다.
 * @param {string} path - 확인할 URL 경로
 * @returns {string|null} - 활성 메뉴 키 또는 null
 */
function getActiveMenuKey(path) {
  if (
    path.startsWith('/approval/drafter') ||
    path.startsWith('/approval/approver') ||
    path.startsWith('/approval/reference') ||
    path.startsWith('/approval/execution')
  ) {
    return 'approvalBox';
  }
  if (path.startsWith('/approval/temporary-archive')) {
    return 'archiveBox';
  }
  if (path.startsWith('/approval/vacation')) {
    return 'vacationManagement';
  }
  if (path.startsWith('/approval/settings')) {
    return 'settings';
  }
  return null;
}

/**
 * 경로가 같은 메뉴 그룹에 속하는지 확인
 * @param {string} path1 - 첫 번째 경로
 * @param {string} path2 - 두 번째 경로
 * @returns {boolean} 같은 메뉴 그룹에 속하면 true
 */
function isSameMenuGroup(path1, path2) {
  return getActiveMenuKey(path1) === getActiveMenuKey(path2);
}

/**
 * Sidebar 컴포넌트
 * - 좌측 네비게이션 메뉴
 * - 드롭다운 메뉴 지원
 */
export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { open: openApprovalGuideModal } = useApprovalGuideModal();
  // 문의사항 오픈 여부
  const [isInquiryOpen, setIsInquiryOpen] = useState(false);
  const { isOpen, toggle: toggleSidebar } = useSidebarStore();
  const isMobile = useIsMobile();
  const prevPathRef = useRef(location.pathname);

  const [alertState, setAlertState] = useState({
    isOpen: false,
    message: '',
  });

  // 컴포넌트가 로드될 때 현재 URL을 기반으로 메뉴의 초기 상태를 설정
  const [openMenus, setOpenMenus] = useState(() => {
    const activeMenuKey = getActiveMenuKey(location.pathname);
    return {
      approvalBox: activeMenuKey === 'approvalBox',
      archiveBox: activeMenuKey === 'archiveBox',
      vacationManagement: activeMenuKey === 'vacationManagement',
      settings: activeMenuKey === 'settings',
    };
  });

  // URL이 변경될 때 메뉴 상태를 동기화
  useEffect(() => {
    const path = location.pathname;
    const prevPath = prevPathRef.current;

    // 같은 메뉴 그룹 내에서 이동하는 경우, 상태 업데이트 불필요
    if (isSameMenuGroup(path, prevPath)) {
      prevPathRef.current = path;
      return;
    }

    // 다른 메뉴 그룹으로 이동한 경우, 상태 업데이트
    const targetMenuKey = getActiveMenuKey(path);
    setOpenMenus({
      approvalBox: targetMenuKey === 'approvalBox',
      archiveBox: targetMenuKey === 'archiveBox',
      vacationManagement: targetMenuKey === 'vacationManagement',
      settings: targetMenuKey === 'settings',
    });

    prevPathRef.current = path;
  }, [location.pathname]);

  /**
   * 드롭다운 메뉴의 열림/닫힘 상태를 토글합니다. (아코디언 방식)
   * @param {string} menuKey - 토글할 메뉴의 키
   */
  function toggleMenu(menuKey) {
    setOpenMenus((prev) => {
      const isAlreadyOpen = prev[menuKey];
      // 모든 메뉴를 닫고, 현재 메뉴의 상태만 반전시킴
      return {
        approvalBox: false,
        archiveBox: false,
        vacationManagement: false,
        settings: false,
        [menuKey]: !isAlreadyOpen,
      };
    });
  }

  /**
   * 지정된 경로로 페이지를 이동합니다.
   * @param {string} path - 이동할 라우트 경로
   * @param {Event} event - 이벤트 객체 (선택적)
   */
  function handleMenuClick(path, event) {
    // 하위메뉴 클릭 시 이벤트 전파 방지
    if (event) {
      event.stopPropagation();
    }
    navigate(path);
  }

  /**
   * 현재 URL 경로가 주어진 경로와 일치하는지 확인합니다.
   * @param {string} path - 비교할 라우트 경로
   * @returns {boolean} 현재 경로와 일치하면 true, 아니면 false
   */
  function isActiveMenu(path) {
    return location.pathname === path;
  }

  return (
    <>
      {/* 사이드바 토글 버튼 - 사이드바 바깥에 위치하여 항상 표시 */}
      <button
        type="button"
        className={`${styles.toggleButton} ${!isOpen ? styles.toggleButtonClosed : ''}`}
        onClick={toggleSidebar}
        aria-label={isOpen ? '사이드바 닫기' : '사이드바 열기'}
      >
        <img
          src={ChevronRightIcon}
          alt=""
          className={`${styles.toggleIcon} ${isOpen ? styles.toggleIconOpen : ''}`}
        />
      </button>

      <aside className={`${styles.sidebar} ${!isOpen ? styles.sidebarClosed : ''}`}>
        <nav className={styles.nav}>
          {/* 새문서 작성 버튼 - 모바일에서는 숨김 */}
          {!isMobile && (
            <button
              type="button"
              className={styles.newDocumentButton}
              onClick={() => handleMenuClick('/approval/new-document')}
            >
              <span className={styles.newDocumentText}>새문서 작성</span>
              <span className={styles.plusIcon} />
            </button>
          )}

          {/* 결재함 */}
          <div className={styles.menuGroup}>
            <button
              type="button"
              className={styles.menuItem}
              onClick={() => toggleMenu('approvalBox')}
            >
              <span className={styles.menuLeft}>
                <span className={styles.menuIcon}>
                  <img src={InboxIcon} alt="결재함" />
                </span>
                <span className={styles.menuText}>
                  결재함
                  <ApprovalNotificationDot />
                </span>
              </span>
              <span
                className={`${styles.chevron} ${openMenus.approvalBox ? styles.chevronOpen : ''}`}
              />
            </button>
            <div className={`${styles.subMenu} ${openMenus.approvalBox ? styles.subMenuOpen : ''}`}>
              <button
                type="button"
                className={`${styles.subMenuItem} ${isActiveMenu('/approval/approver') ? styles.subMenuItemActive : ''}`}
                onClick={(e) => handleMenuClick('/approval/approver', e)}
              >
                <span className={styles.subMenuInner}>
                  <span className={styles.indicator} />
                  <span className={styles.subMenuText}>
                    결재문서
                    <ApprovalNotificationDot />
                  </span>
                </span>
              </button>
              <button
                type="button"
                className={`${styles.subMenuItem} ${isActiveMenu('/approval/drafter') ? styles.subMenuItemActive : ''}`}
                onClick={(e) => handleMenuClick('/approval/drafter', e)}
              >
                <span className={styles.subMenuInner}>
                  <span className={styles.indicator} />
                  <span className={styles.subMenuText}>기안문서</span>
                </span>
              </button>

              <button
                type="button"
                className={`${styles.subMenuItem} ${isActiveMenu('/approval/reference') ? styles.subMenuItemActive : ''}`}
                onClick={(e) => handleMenuClick('/approval/reference', e)}
              >
                <span className={styles.subMenuInner}>
                  <span className={styles.indicator} />
                  <span className={styles.subMenuText}>참조문서</span>
                </span>
              </button>
              <button
                type="button"
                className={`${styles.subMenuItem} ${isActiveMenu('/approval/execution') ? styles.subMenuItemActive : ''}`}
                onClick={(e) => handleMenuClick('/approval/execution', e)}
              >
                <span className={styles.subMenuInner}>
                  <span className={styles.indicator} />
                  <span className={styles.subMenuText}>시행문서</span>
                </span>
              </button>
            </div>
          </div>

          {/* 보관함 */}
          <div className={styles.menuGroup}>
            <button
              type="button"
              className={styles.menuItem}
              onClick={() => toggleMenu('archiveBox')}
            >
              <span className={styles.menuLeft}>
                <span className={styles.menuIcon}>
                  <img src={BoxIcon} alt="보관함" />
                </span>
                <span className={styles.menuText}>보관함</span>
              </span>
              <span
                className={`${styles.chevron} ${openMenus.archiveBox ? styles.chevronOpen : ''}`}
              />
            </button>
            <div className={`${styles.subMenu} ${openMenus.archiveBox ? styles.subMenuOpen : ''}`}>
              <button
                type="button"
                className={`${styles.subMenuItem} ${isActiveMenu('/approval/temporary-archive') ? styles.subMenuItemActive : ''}`}
                onClick={(e) => handleMenuClick('/approval/temporary-archive', e)}
              >
                <span className={styles.subMenuInner}>
                  <span className={styles.indicator} />
                  <span className={styles.subMenuText}>임시보관함</span>
                </span>
              </button>
            </div>
          </div>

          {/* 휴가관리 */}
          <div className={styles.menuGroup}>
            <button
              type="button"
              className={styles.menuItem}
              onClick={() => toggleMenu('vacationManagement')}
            >
              <span className={styles.menuLeft}>
                <span className={styles.menuIcon}>
                  <img src={UmbrellaIcon} alt="휴가관리" />
                </span>
                <span className={styles.menuText}>휴가관리</span>
              </span>
              <span
                className={`${styles.chevron} ${openMenus.vacationManagement ? styles.chevronOpen : ''}`}
              />
            </button>
            <div
              className={`${styles.subMenu} ${openMenus.vacationManagement ? styles.subMenuOpen : ''}`}
            >
              <button
                type="button"
                className={`${styles.subMenuItem} ${isActiveMenu('/approval/vacation/request') ? styles.subMenuItemActive : ''}`}
                onClick={(e) => handleMenuClick('/approval/vacation/request', e)}
              >
                <span className={styles.subMenuInner}>
                  <span className={styles.indicator} />
                  <span className={styles.subMenuText}>휴가신청</span>
                </span>
              </button>
              <button
                type="button"
                className={`${styles.subMenuItem} ${isActiveMenu('/approval/vacation/approval') ? styles.subMenuItemActive : ''}`}
                onClick={(e) => handleMenuClick('/approval/vacation/approval', e)}
              >
                <span className={styles.subMenuInner}>
                  <span className={styles.indicator} />
                  <span className={styles.subMenuText}>휴가결재</span>
                </span>
              </button>
            </div>
          </div>

          {/* 설정 */}
          <div className={styles.menuGroup}>
            <button
              type="button"
              className={styles.menuItem}
              onClick={() => toggleMenu('settings')}
            >
              <span className={styles.menuLeft}>
                <span className={styles.menuIcon}>
                  <img src={SettingsIcon} alt="설정" />
                </span>
                <span className={styles.menuText}>설정</span>
              </span>
              <span
                className={`${styles.chevron} ${openMenus.settings ? styles.chevronOpen : ''}`}
              />
            </button>
            <div className={`${styles.subMenu} ${openMenus.settings ? styles.subMenuOpen : ''}`}>
              <button
                type="button"
                className={`${styles.subMenuItem} ${isActiveMenu('/approval/settings/sign') ? styles.subMenuItemActive : ''}`}
                onClick={(e) => handleMenuClick('/approval/settings/sign', e)}
              >
                <span className={styles.subMenuInner}>
                  <span className={styles.indicator} />
                  <span className={styles.subMenuText}>서명설정</span>
                </span>
              </button>

              <button
                type="button"
                className={`${styles.subMenuItem} ${isActiveMenu('/approval/settings/password') ? styles.subMenuItemActive : ''}`}
                onClick={(e) => handleMenuClick('/approval/settings/password', e)}
              >
                <span className={styles.subMenuInner}>
                  <span className={styles.indicator} />
                  <span className={styles.subMenuText}>비밀번호 설정</span>
                </span>
              </button>
              <button
                type="button"
                className={`${styles.subMenuItem} ${isActiveMenu('/approval/settings/approval-line') ? styles.subMenuItemActive : ''}`}
                onClick={(e) => handleMenuClick('/approval/settings/approval-line', e)}
              >
                <span className={styles.subMenuInner}>
                  <span className={styles.indicator} />
                  <span className={styles.subMenuText}>자주 쓰는 결재 라인</span>
                </span>
              </button>
              {/* 모바일 환경에서만 알림설정 메뉴 표시 */}
              {isMobile && (
                <button
                  type="button"
                  className={`${styles.subMenuItem} ${isActiveMenu('/approval/settings/notification') ? styles.subMenuItemActive : ''}`}
                  onClick={(e) => handleMenuClick('/approval/settings/notification', e)}
                >
                  <span className={styles.subMenuInner}>
                    <span className={styles.indicator} />
                    <span className={styles.subMenuText}>알림설정</span>
                  </span>
                </button>
              )}
            </div>
          </div>
        </nav>

        {/* 사이드바 푸터 */}
        <footer className={styles.sidebarFooter}>
          <div className={styles.footerLinkGroup}>
            <button
              type="button"
              className={styles.footerLink}
              onClick={() => openApprovalGuideModal()}
            >
              결제 라인 안내
            </button>
          </div>
          <span className={styles.inquiryDivider} />
          <button
            type="button"
            className={styles.footerLink}
            onClick={() => setIsInquiryOpen(true)}
          >
            불편/개선요청
          </button>
          <span className={styles.versionDivider} />
          <button type="button" className={`${styles.footerLink} ${styles.versionInfo}`}>
            버전 정보
          </button>
        </footer>
      </aside>

      <Modal
        isOpen={isInquiryOpen}
        onClose={() => setIsInquiryOpen(false)}
        // title="문의 사항"
        size="medium"
        closeOnOverlayClick={false}
        showCloseButton
        classes={{
          modalContent: styles.inquiryModalContent,
        }}
      >
        <TabContentLayout
          tabs={[
            {
              value: 'write',
              label: '접수하기',
              content: (
                <InquiryFormModal
                  onClose={() => setIsInquiryOpen(false)}
                  onSuccess={() => {
                    setAlertState({
                      isOpen: true,
                      message: '문의가 등록되었습니다.',
                    });
                    setIsInquiryOpen(false);
                  }}
                  onError={(message) => {
                    setAlertState({
                      isOpen: true,
                      message,
                    });
                  }}
                />
              ),
            },
            {
              value: 'inquiryList',
              label: '접수내역',
              content: <InquiryListModal />,
            },
          ]}
          defaultTab="write"
        />
      </Modal>
      <Alert
        isOpen={alertState.isOpen}
        message={alertState.message}
        onClose={() =>
          setAlertState((prev) => ({
            ...prev,
            isOpen: false,
          }))
        }
      />
    </>
  );
}
